import React, { useCallback, useMemo, useState } from 'react';
import { getGlobal } from 'reactn';
import { Box, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { DataGridWithHeaders } from '@orijinworks/frontend-commons';
import RoleAssignmentDialog from '../roles-assignments-modal';
import {
  deleteRoleAssignment,
  addRolesAssignment,
} from '../../../../util/APIUtils';
import { isPermitted } from '../../../permission-control/permission-control';
import { FacilityStatusCell } from './facility-status-cell';
import CustomMenu from '../../menu';

const FACILITY_STATUS_FILTER_OPTIONS = ['Active', 'Inactive'];

/** Natural / locale-aware name order (e.g. "Site 2" before "Site 10"). */
function compareLocationNamesNatural(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * Facilities grid: active assignments first, then inactive; within each group, sort by location name.
 * Immutable — returns a new array.
 * @param {Array<{ active?: boolean, locationName?: string }>} locations
 * @returns {Array}
 */
export function sortFacilitiesLocationsByActiveThenName(locations) {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return [...locations].sort((x, y) => {
    const xActive = Boolean(x.active);
    const yActive = Boolean(y.active);
    if (xActive !== yActive) {
      return xActive ? -1 : 1;
    }
    return compareLocationNamesNatural(x.locationName, y.locationName);
  });
}

const FACILITIES_GRID_COLUMNS = [
  {
    name: 'locationName',
    displayName: 'Location Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'facilityStatus',
    displayName: 'Status',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    type: 'override',
    filter: { type: 'MultiTagCell', showBlankFilter: false },
    options: FACILITY_STATUS_FILTER_OPTIONS,
  },
  {
    name: 'actions',
    displayName: 'Actions',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'override',
  },
];

/** When true, do not render the facility row actions menu for this row (permissions + active-count rules). */
function shouldHideFacilityRowActionsMenu(loc, locations) {
  const g = getGlobal();
  const countActive = locations.filter(l => l.active).length;
  if (countActive === 0) return true;
  if (loc.active) {
    return !isPermitted(g.cbacConfigFlag, g.permissions, 'Unassign_Admin_From_Different_Location');
  }
  return !isPermitted(g.cbacConfigFlag, g.permissions, 'Assign_Admin_To_Different_Location');
}

/**
 * V2 facilities DataGrid UX — CREATE_STAFF_V2 enabled.
 * Grid-only loading; Add Facility dialog from parent when `addFacilityDialogOpen` is wired.
 */
function RolesAssignmentV2({
  classes,
  facilitiesLocations = [],
  userDetail,
  refreshStaffList: refreshStaffListProp,
  onFacilitiesRefresh,
  addFacilityDialogOpen,
  addFacilityDialogForReactivation = false,
  onAddFacilityDialogClose,
  onReactivationFacilitySubmit,
  plaRoleId,
  isViewingOwnProfile = false,
}) {
  const [facilitiesGridLoading, setFacilitiesGridLoading] = useState(false);
  const [isNewAssignmentDialogOpen, setIsNewAssignmentDialogOpen] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const refreshStaffList = useCallback(() => refreshStaffListProp?.(), [refreshStaffListProp]);

  const withGridLoading = useCallback(async fn => {
    setFacilitiesGridLoading(true);
    try {
      await fn();
    } finally {
      setFacilitiesGridLoading(false);
    }
  }, []);

  const showOpError = useCallback((msg = 'Operation failed, please contact administrator.') => {
    setErrorMsg(msg);
    setShowErrorMsg(true);
  }, []);

  const sortedFacilitiesLocations = useMemo(
    () => sortFacilitiesLocationsByActiveThenName(facilitiesLocations),
    [facilitiesLocations]
  );

  const handleSetToInactive = useCallback(
    async row => {
      const locations = sortedFacilitiesLocations;
      const countActive = locations.filter(l => l.active).length;
      if (row.active && countActive <= 1) {
        showOpError('Admins must have at least one active facility.');
        return;
      }
      try {
        await withGridLoading(async () => {
          await deleteRoleAssignment(row.locationId, userDetail.userName);
          if (onFacilitiesRefresh) await onFacilitiesRefresh();
          refreshStaffList();
        });
      } catch {
        showOpError();
      }
    },
    [sortedFacilitiesLocations, userDetail.userName, onFacilitiesRefresh, refreshStaffList, withGridLoading, showOpError]
  );

  const handleSetToActive = useCallback(
    async row => {
      // Janus POST /user/roles/location requires roleId when re-activating a facility.
      const roleId = plaRoleId;
      if (roleId == null) {
        showOpError('Role is required to activate this facility. Refresh the page and try again.');
        return;
      }
      try {
        await withGridLoading(async () => {
          await addRolesAssignment({
            locationId: row.locationId,
            userName: userDetail.userName,
            roleId,
          });
          if (onFacilitiesRefresh) await onFacilitiesRefresh();
          refreshStaffList();
        });
      } catch (error) {
        showOpError(
          error.status === 409 ? 'This location is already assigned to the staff' : 'Operation failed, please contact administrator.'
        );
      }
    },
    [userDetail.userName, plaRoleId, onFacilitiesRefresh, refreshStaffList, withGridLoading, showOpError]
  );

  const getFacilityRowActions = useCallback(
    (loc, locations) => {
      if (shouldHideFacilityRowActionsMenu(loc, locations)) return [];

      const countActive = locations.filter(l => l.active).length;
      if (loc.active) {
        return [
          {
            title: 'Set to Inactive',
            onClick: async () => {
              if (countActive <= 1) {
                showOpError('Admins must have at least one active facility.');
                return;
              }
              await handleSetToInactive(loc);
            },
          },
        ];
      }

      return [{ title: 'Set to Active', onClick: () => handleSetToActive(loc) }];
    },
    [handleSetToInactive, handleSetToActive, showOpError]
  );

  const showFacilitiesActionsColumn = useMemo(() => {
    if (isViewingOwnProfile) return false;
    const locs = sortedFacilitiesLocations;
    return locs.some(loc => !shouldHideFacilityRowActionsMenu(loc, locs));
  }, [sortedFacilitiesLocations, isViewingOwnProfile]);

  const facilitiesDataGridColumns = useMemo(
    () =>
      FACILITIES_GRID_COLUMNS.map(col =>
        col.name === 'actions' ? { ...col, isVisible: showFacilitiesActionsColumn } : col
      ),
    [showFacilitiesActionsColumn]
  );

  const facilitiesGridRows = useMemo(() => {
    const locs = sortedFacilitiesLocations;
    return locs.map(loc => ({
      isSelected: false,
      rowData: {
        id: loc.locationId,
        roleId: loc.roleId,
        locationName: loc.locationName || '',
        facilityStatus: () => <FacilityStatusCell active={loc.active} classes={classes} />,
        actions: () => {
          if (isViewingOwnProfile || shouldHideFacilityRowActionsMenu(loc, locs)) return null;
          return <CustomMenu options={getFacilityRowActions(loc, locs)} />;
        },
      },
      preDefineComponentProps: {
        facilityStatus: { tags: [{ label: loc.active ? 'Active' : 'Inactive' }] },
      },
    }));
  }, [sortedFacilitiesLocations, classes, getFacilityRowActions, isViewingOwnProfile]);

  const submitAssignment = useCallback(
    async values => {
      const payload = {
        locationId: values.location,
        userName: userDetail.userName,
        roleId: values.roles,
      };
      if (addFacilityDialogForReactivation && onReactivationFacilitySubmit) {
        await withGridLoading(async () => {
          await onReactivationFacilitySubmit(payload);
        });
        return;
      }
      try {
        await withGridLoading(async () => {
          await addRolesAssignment(payload);
          if (onFacilitiesRefresh) await onFacilitiesRefresh();
          refreshStaffList();
        });
      } catch (error) {
        showOpError(
          error.status === 409 ? 'This location is already assigned to the staff' : 'Operation failed, please contact administrator.'
        );
      }
    },
    [
      userDetail.userName,
      onFacilitiesRefresh,
      refreshStaffList,
      withGridLoading,
      showOpError,
      addFacilityDialogForReactivation,
      onReactivationFacilitySubmit,
    ]
  );

  const handleCloseErrorMsg = useCallback(() => {
    setShowErrorMsg(false);
    setErrorMsg('');
  }, []);

  const dialogOpen =
    addFacilityDialogOpen !== undefined ? addFacilityDialogOpen : isNewAssignmentDialogOpen;

  const handleDialogClose = useCallback(() => {
    if (onAddFacilityDialogClose) onAddFacilityDialogClose();
    setIsNewAssignmentDialogOpen(false);
  }, [onAddFacilityDialogClose]);

  const handleRefreshGrid = useCallback(async () => {
    if (!onFacilitiesRefresh) return;
    setFacilitiesGridLoading(true);
    try {
      await onFacilitiesRefresh();
    } finally {
      setFacilitiesGridLoading(false);
    }
  }, [onFacilitiesRefresh]);

  return (
    <>
      <Snackbar
        open={showErrorMsg}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseErrorMsg}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {errorMsg}
        </MuiAlert>
      </Snackbar>

      <Box width="100%">
        <Box className={classes.facilitiesGridWrap} boxShadow={3}>
          <DataGridWithHeaders
            title=""
            subtitle=""
            dataGridProps={{
              columns: facilitiesDataGridColumns,
              data: [...facilitiesGridRows],
              pageSize: 10,
              pageSizeOptions: [5, 10, 20, 50, 100, 200],
              paginationMode: 'client',
              isLoading: facilitiesGridLoading,
              showCheckbox: false,
              onRefreshHandler: handleRefreshGrid,
              onSelectionChange: () => {},
            }}
          />
        </Box>

        <RoleAssignmentDialog
          open={dialogOpen}
          handleClose={handleDialogClose}
          userDetail={userDetail}
          submitAssignment={submitAssignment}
          useAddFacilityOnly
          createStaffV2Enabled
          plaRoleId={plaRoleId}
        />
      </Box>
    </>
  );
}

RolesAssignmentV2.defaultProps = {
  facilitiesLocations: [],
  onFacilitiesRefresh: null,
  plaRoleId: null,
  addFacilityDialogForReactivation: false,
  onReactivationFacilitySubmit: null,
};

export default RolesAssignmentV2;
